import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import storeApi from '../services/storeApi';
import authService from '../../services/api/authService';
import messagingService from '../../services/messagingService';
import { useToast } from '../../contexts/ToastContext';

/**
 * AccountPage - User account dashboard with orders, profile, etc.
 * Uses authService for current user (RULE: centralize API usage).
 */
const AccountPage = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [startingConversationOrderId, setStartingConversationOrderId] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/store/login-register?return=/store/account');
        return;
      }

      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);

        try {
          const ordersData = await storeApi.getOrders();
          setOrders(Array.isArray(ordersData) ? ordersData : (ordersData.results || []));
        } catch (orderError) {
          setOrders([]);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          navigate('/store/login-register?return=/store/account');
        } else {
          showError('Failed to load account information');
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, showError]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.dispatchEvent(new Event('userLoggedOut'));
    navigate('/store');
  };

  const handleMessageOrder = async (orderId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setStartingConversationOrderId(orderId);
    try {
      const data = await messagingService.startConversationFromOrder(orderId);
      const conversationId = data?.id;
      if (conversationId) {
        navigate('/messages/' + conversationId);
      } else {
        showError('Could not start conversation. Please try again.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to start conversation';
      showError(msg);
    } finally {
      setStartingConversationOrderId(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p>Loading account information...</p>
        </div>
      </PageContainer>
    );
  }

  if (!user) {
    return null; // Will redirect
  }

  return (
    <PageContainer>
      {/* Breadcrumb */}
      <div className="breadcrumb-area">
        <div className="container">
          <div className="breadcrumb-content">
            <ul>
              <li><Link to="/store">Home</Link></li>
              <li className="active">My Account</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Account Area */}
      <div className="account-area pt-60 pb-30">
        <div className="container">
          <div className="row">
            <div className="col-lg-3 col-md-12">
              {/* Account Sidebar */}
              <div className="account-sidebar">
                <div className="account-user-info">
                  <div className="account-user-avatar">
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: '#3b82f6',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px',
                      fontWeight: 'bold',
                      margin: '0 auto 15px'
                    }}>
                      {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <h4 style={{ textAlign: 'center', marginBottom: '5px' }}>
                      {user.first_name && user.last_name 
                        ? `${user.first_name} ${user.last_name}`
                        : user.email?.split('@')[0] || 'User'}
                    </h4>
                    <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="account-menu">
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    <li>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveTab('orders');
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 15px',
                          border: 'none',
                          background: activeTab === 'orders' ? '#3b82f6' : 'transparent',
                          color: activeTab === 'orders' ? '#fff' : '#333',
                          cursor: 'pointer',
                          marginBottom: '5px',
                          borderRadius: '4px'
                        }}
                      >
                        My Orders
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveTab('profile');
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 15px',
                          border: 'none',
                          background: activeTab === 'profile' ? '#3b82f6' : 'transparent',
                          color: activeTab === 'profile' ? '#fff' : '#333',
                          cursor: 'pointer',
                          marginBottom: '5px',
                          borderRadius: '4px'
                        }}
                      >
                        Account Details
                      </button>
                    </li>
                    <li>
                      <Link
                        to="/store/wishlist"
                        style={{
                          display: 'block',
                          padding: '12px 15px',
                          color: '#333',
                          textDecoration: 'none',
                          marginBottom: '5px',
                          borderRadius: '4px'
                        }}
                      >
                        My Wishlist
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/store/cart"
                        style={{
                          display: 'block',
                          padding: '12px 15px',
                          color: '#333',
                          textDecoration: 'none',
                          marginBottom: '5px',
                          borderRadius: '4px'
                        }}
                      >
                        Shopping Cart
                      </Link>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLogout();
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 15px',
                          border: 'none',
                          background: 'transparent',
                          color: '#e80f0f',
                          cursor: 'pointer',
                          marginTop: '20px',
                          borderRadius: '4px'
                        }}
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-lg-9 col-md-12">
              <div className="account-content">
                {activeTab === 'orders' && (
                  <div className="account-orders">
                    <h3 style={{ marginBottom: '20px' }}>My Orders</h3>
                    {orders.length === 0 ? (
                      <div className="text-center py-5">
                        <p>You haven't placed any orders yet.</p>
                        <Link to="/store/shop" className="btn" style={{ marginTop: '15px', display: 'inline-block' }}>
                          Start Shopping
                        </Link>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                              <th style={{ padding: '12px', textAlign: 'left' }}>Order ID</th>
                              <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                              <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                              <th style={{ padding: '12px', textAlign: 'left' }}>Total</th>
                              <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map((order) => (
                              <tr key={order.id} style={{ borderBottom: '1px solid #e5e5e5' }}>
                                <td style={{ padding: '12px' }}>#{order.id}</td>
                                <td style={{ padding: '12px' }}>
                                  {new Date(order.created_at).toLocaleDateString()}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <span style={{
                                    padding: '4px 12px',
                                    borderRadius: '4px',
                                    background: order.status === 'completed' ? '#10b981' : '#3b82f6',
                                    color: '#fff',
                                    fontSize: '12px'
                                  }}>
                                    {order.status || 'Pending'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px' }}>
                                  PKR {parseFloat(order.total_price || 0).toFixed(2)}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <Link
                                    to={`/store/orders/${order.id}`}
                                    style={{ color: '#3b82f6', textDecoration: 'none', marginRight: '12px' }}
                                  >
                                    View
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={(ev) => handleMessageOrder(order.id, ev)}
                                    disabled={startingConversationOrderId === order.id}
                                    style={{
                                      color: '#3b82f6',
                                      background: 'none',
                                      border: 'none',
                                      cursor: startingConversationOrderId === order.id ? 'wait' : 'pointer',
                                      padding: 0,
                                      textDecoration: 'none',
                                      fontSize: 'inherit',
                                    }}
                                  >
                                    {startingConversationOrderId === order.id ? 'Opening...' : 'Message'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'profile' && (
                  <div className="account-profile">
                    <h3 style={{ marginBottom: '20px' }}>Account Details</h3>
                    <div className="profile-info" style={{ background: '#f9f9f9', padding: '20px', borderRadius: '4px' }}>
                      <div style={{ marginBottom: '15px' }}>
                        <strong>Email:</strong> {user.email}
                      </div>
                      {user.first_name && (
                        <div style={{ marginBottom: '15px' }}>
                          <strong>First Name:</strong> {user.first_name}
                        </div>
                      )}
                      {user.last_name && (
                        <div style={{ marginBottom: '15px' }}>
                          <strong>Last Name:</strong> {user.last_name}
                        </div>
                      )}
                      {user.phone && (
                        <div style={{ marginBottom: '15px' }}>
                          <strong>Phone:</strong> {user.phone}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default AccountPage;

